import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureUpgradeModal } from '../contexts/FeatureUpgradeModalContext';
import { Button } from '../components/ui';
import ProfileSettingsTab from '../components/settings/ProfileSettingsTab';
import BillingSettingsTab from '../components/settings/BillingSettingsTab';
import PriceTiersSettingsTab from '../components/settings/PriceTiersSettingsTab';
import BillingProfilesTab from '../components/settings/BillingProfilesTab';
import SubscriptionTab from '../components/settings/SubscriptionTab';
import EmailSettingsTab from '../components/settings/EmailSettingsTab';

// Internal type for logic - keep as is
type TabType = 'Profile' | 'Billing' | 'Price Tiers' | 'Billing Profiles' | 'Email' | 'Subscription';

// TabButton component definition
const TabButton: React.FC<{
  tab: TabType;
  activeTab: TabType;
  onClick: (tab: TabType) => void;
  t: (key: string) => string;
}> = ({ tab, activeTab, onClick, t }) => {
  const isActive = activeTab === tab;
  return (
    <button
      onClick={() => onClick(tab)}
      className={`px-4 py-2 text-sm font-medium rounded-md ${
        isActive
          ? 'bg-indigo-100 text-indigo-700'
          : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
      }`}
    >
      {t(`settings.tabs.${tab.toLowerCase().replace(' ', '')}`)}
    </button>
  );
};

export const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { therapist } = useAuth();
  const { openUpgradeModal } = useFeatureUpgradeModal();
  const [activeTab, setActiveTab] = useState<TabType>('Profile');

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg border border-[#EAECF0] p-2">
        <div className="flex space-x-2">
          <TabButton
            tab="Profile"
            activeTab={activeTab}
            onClick={setActiveTab}
            t={t}
          />
          <TabButton
            tab="Billing Profiles"
            activeTab={activeTab}
            onClick={() => {
              const hasBillingAccess = therapist?.packageFeaturesSnapshot?.BILLING_ACCESS_ENABLED;
              if (therapist && typeof hasBillingAccess === 'boolean' && !hasBillingAccess) {
                openUpgradeModal({
                  featureNameKey: 'featureUpgradeModal.featureNames.billingAccess',
                  customMessageKey: 'featureUpgradeModal.messages.billingAccessDisabled',
                });
              } else if (therapist && typeof hasBillingAccess === 'boolean' && hasBillingAccess) {
                setActiveTab('Billing Profiles');
              } else if (!therapist) {
                console.warn('Therapist data not available to check billing access.');
              }
            }}
            t={t}
          />
          {/* Email tab after Billing Profiles, before Billing */}
          <TabButton
            tab="Email"
            activeTab={activeTab}
            onClick={() => {
              const hasEmailAccess = therapist?.packageFeaturesSnapshot?.EMAIL_NOTIFICATIONS;
              if (therapist && typeof hasEmailAccess === 'boolean' && !hasEmailAccess) {
                openUpgradeModal({
                  featureNameKey: 'featureUpgradeModal.featureNames.emailNotifications',
                  customMessageKey: 'featureUpgradeModal.messages.emailNotifications',
                });
              } else if (therapist && typeof hasEmailAccess === 'boolean' && hasEmailAccess) {
                setActiveTab('Email');
              } else if (!therapist) {
                console.warn('Therapist data not available to check email access.');
              }
            }}
            t={t}
          />
          <TabButton
            tab="Billing"
            activeTab={activeTab}
            onClick={setActiveTab}
            t={t}
          />
          <TabButton
            tab="Price Tiers"
            activeTab={activeTab}
            onClick={setActiveTab}
            t={t}
          />
          <TabButton
            tab="Subscription"
            activeTab={activeTab}
            onClick={setActiveTab}
            t={t}
          />
        </div>
      </div>

      {/* Tab content */}
      {activeTab === 'Profile' && <ProfileSettingsTab />}
      {activeTab === 'Billing' && <BillingSettingsTab />}
      {activeTab === 'Price Tiers' && <PriceTiersSettingsTab />}
      {activeTab === 'Billing Profiles' && <BillingProfilesTab />}
      {/* Email tab content after Billing Profiles */}
      {activeTab === 'Email' && <EmailSettingsTab />}
      {activeTab === 'Subscription' && <SubscriptionTab />}
    </div>
  );
};

export default SettingsPage;
